import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["input", "messages", "taskList", "messageList", "messagesLoader", "tasksLoader"]
  static values = { stateKey: String }

  connect() {
    this.requests = new Map()
    this.loadFrame = null
    this.initialScrollRestored = false
    const state = this.readState()
    this.followLatest = state?.followLatest !== false
    this.observeMessages()
    requestAnimationFrame(() => {
      if (!this.element.isConnected) return
      if (this.hasTaskListTarget) this.taskListTarget.scrollTop = state?.taskListTop || 0
      if (this.hasMessagesTarget && !this.followLatest) {
        this.messagesTarget.scrollTop = state?.messagesTop || 0
      } else if (this.hasMessagesTarget) {
        this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
      }
      this.initialScrollRestored = true
      this.scheduleLoads()
    })

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.element.classList.remove("assistant-workspace-modal--entering")
    } else if (this.element.classList.contains("assistant-workspace-modal--entering")) {
      this.openingTimer = window.setTimeout(() => this.finishOpening(), 360)
    }
  }

  disconnect() {
    this.messageObserver?.disconnect()
    this.requests?.forEach(request => request.abort())
    cancelAnimationFrame(this.loadFrame)
    window.clearTimeout(this.closeTimer)
    window.clearTimeout(this.openingTimer)
  }

  close(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    event.preventDefault()
    this.rememberState()
    this.requests?.forEach(request => request.abort())
    const panelClose = new CustomEvent("assistant-panel:close", { bubbles: true, cancelable: true })
    if (!this.element.dispatchEvent(panelClose)) return

    if (this.element.classList.contains("is-closing")) return

    const returnUrl = event.currentTarget.href
    window.clearTimeout(this.openingTimer)
    this.element.classList.remove("assistant-workspace-modal--entering")
    this.element.classList.add("is-closing")

    const duration = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 220
    this.closeTimer = window.setTimeout(() => Turbo.visit(returnUrl), duration)
  }

  finishOpening(event) {
    if (event?.animationName && event.animationName !== "assistant-workspace-popup-in") return

    window.clearTimeout(this.openingTimer)
    this.element.classList.remove("assistant-workspace-modal--entering")
  }

  inputTargetConnected(element) {
    requestAnimationFrame(() => {
      this.resizeElement(element)

      if (!element.disabled && element.closest("dialog.assistant-panel")?.open !== false) {
        element.focus({ preventScroll: true })
      }
    })
  }

  resize(event) {
    this.resizeElement(event.currentTarget)
  }

  submitOnEnter(event) {
    if (event.key !== "Enter" || event.shiftKey || event.isComposing) return

    event.preventDefault()
    const form = event.currentTarget.form
    if (form?.getAttribute("aria-busy") === "true") return

    const hasAttachment = form?.querySelector('input[name="assistant_message[attachments][]"]')
    if (event.currentTarget.value.trim() === "" && !hasAttachment) return

    form?.requestSubmit()
  }

  observeMessages() {
    if (!this.hasMessagesTarget) return

    this.messageObserver?.disconnect()
    this.messageObserver = new MutationObserver(() => {
      if (this.followLatest) this.scrollToLatest()
      this.scheduleLoads()
    })
    this.messageObserver.observe(this.hasMessageListTarget ? this.messageListTarget : this.messagesTarget, { childList: true, subtree: true })
  }

  scrollToLatest(smooth = true) {
    if (!this.hasMessagesTarget) return

    requestAnimationFrame(() => {
      if (!this.element.isConnected || !this.hasMessagesTarget || !this.followLatest) return
      this.messagesTarget.scrollTo({
        top: this.messagesTarget.scrollHeight,
        behavior: smooth ? "smooth" : "auto"
      })
    })
  }

  trackScroll(event) {
    if (this.hasMessagesTarget && event.currentTarget === this.messagesTarget) {
      const { scrollHeight, scrollTop, clientHeight } = this.messagesTarget
      this.followLatest = scrollHeight - scrollTop - clientHeight < 80
    }
    this.rememberState()
    this.scheduleLoads()
  }

  scheduleLoads() {
    if (this.loadFrame || !this.initialScrollRestored) return
    this.loadFrame = requestAnimationFrame(() => {
      this.loadFrame = null
      if (!this.element.isConnected) return
      if (this.hasMessagesLoaderTarget && this.hasMessageListTarget && this.messagesTarget.clientHeight > 0 && this.messagesTarget.scrollTop < 120) {
        this.loadBatch("messages")
      }
      if (this.hasTasksLoaderTarget && this.taskListTarget.clientHeight > 0 &&
        this.taskListTarget.scrollHeight - this.taskListTarget.scrollTop - this.taskListTarget.clientHeight < 120) {
        this.loadBatch("tasks")
      }
    })
  }

  async loadBatch(kind) {
    const olderMessages = kind === "messages"
    const loader = olderMessages ? this.messagesLoaderTarget : this.tasksLoaderTarget
    const url = loader.dataset.nextUrl
    if (!url || this.requests.has(kind)) return

    const request = new AbortController()
    this.requests.set(kind, request)
    loader.textContent = olderMessages ? "Loading earlier messages…" : "Loading more tasks…"
    loader.setAttribute("aria-busy", "true")
    let loaded = false
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" }, signal: request.signal })
      if (!response.ok) throw new Error("Assistant history request failed")
      const batch = await response.json()
      if (!this.element.isConnected || request.signal.aborted) return
      if (typeof batch.html !== "string" || batch.next_url === url) throw new Error("Invalid assistant history response")

      const container = olderMessages ? this.messagesTarget : this.taskListTarget
      const list = olderMessages ? this.messageListTarget : this.taskListTarget
      // Measure after the request: the reader may have moved while it loaded.
      const anchor = olderMessages ? [...list.children].find(item => item.getBoundingClientRect().bottom > container.getBoundingClientRect().top) : null
      const anchorTop = anchor?.getBoundingClientRect().top
      const template = document.createElement("template")
      template.innerHTML = batch.html
      const existingIds = new Set([...list.children].map(item => item.id))
      for (const item of [...template.content.children]) {
        if (item.id && existingIds.has(item.id)) item.remove()
      }
      if (olderMessages) list.prepend(template.content)
      else loader.before(template.content)
      loader.dataset.nextUrl = batch.next_url || ""
      loader.textContent = ""
      if (anchor) container.scrollTop += anchor.getBoundingClientRect().top - anchorTop
      this.rememberState()
      loaded = true
    } catch (error) {
      if (request.signal.aborted) loader.textContent = ""
      else if (this.element.isConnected) {
        loader.textContent = olderMessages ? "Earlier messages could not load. Scroll to retry." : "More tasks could not load. Scroll to retry."
      }
    } finally {
      if (this.requests.get(kind) === request) {
        this.requests.delete(kind)
        loader.removeAttribute("aria-busy")
        if (loaded) this.scheduleLoads()
      }
    }
  }

  followResponse(event) {
    if (!event.target.matches(".assistant-composer__form")) return

    this.followLatest = true
    this.rememberState()
    this.scrollToLatest(false)
  }

  readState() {
    if (!this.hasStateKeyValue) return null

    try {
      return JSON.parse(sessionStorage.getItem(this.stateKeyValue))
    } catch {
      return null
    }
  }

  rememberState() {
    if (!this.hasStateKeyValue) return

    try {
      sessionStorage.setItem(this.stateKeyValue, JSON.stringify({
        messagesTop: this.hasMessagesTarget ? this.messagesTarget.scrollTop : 0,
        taskListTop: this.hasTaskListTarget ? this.taskListTarget.scrollTop : 0,
        followLatest: this.followLatest
      }))
    } catch {
      // The panel remains usable when browser storage is unavailable.
    }
  }

  resizeElement(element) {
    element.style.height = "auto"
    element.style.height = `${Math.min(element.scrollHeight, 180)}px`
  }
}
